<!-- =========================================================================================
    File Name:
    Description:
    ----------------------------------------------------------------------------------------
    Item Name: GetBoarded UI Template
    Author: GetBoarded
    Author URL: https://www.getboarded.com
========================================================================================== -->

<template>
  <div class="w-full">
    <!-- Popups remain unchanged -->
    <vs-popup fullscreen class="profile-card" title="Presentation Show" :active.sync="flag">
      <iframe
          class="editor-iframe"
          title="Coaching and Mentoring as levers of conscious leadership"
          :src="psrc"
          tabindex="0"
          data-auto-height="true"
          data-aspect-ratio="1.7790927021696252"
          scrolling="no"
          width="100%"
          frameborder="0"
      ></iframe>
    </vs-popup>

    <vs-popup fullscreen class="profile-card" title="Video" :active.sync="flag1">
      <iframe
          class="editor-iframe"
          :src="vsrc"
          style="height: 80vh; padding: 5px; width: 100%;"
          title="YouTube video player"
          frameborder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowfullscreen
      ></iframe>
    </vs-popup>

    <!-- Redesigned Main UI Container -->
    <div class="max-w-7xl mx-auto px-4 py-8">
      
      <!-- Page Header -->
      <div class="mb-8 text-left">
        <h1 class="text-3xl font-bold text-gray-800">{{ $t("trainingCenter") }}</h1>
        <p class="text-gray-500 mt-2 text-sm">Develop at your own pace with tutorials, videos, and training.</p>
      </div>

      <!-- Main Card Enclosure -->
      <vx-card class="shadow-sm rounded-xl overflow-hidden p-2 bg-gray-50">
        <!-- ✨ BEAUTIFUL EMPTY STATE ✨ -->
        <div v-if="isTrainingCenterEmpty" class="py-16 flex flex-col items-center justify-center text-center">
          
          <!-- Clean Feather Icon with soft background -->
          <div class="bg-gray-100 p-6 rounded-full mb-6 shadow-inner">
            <feather-icon icon="InboxIcon" svgClasses="w-16 h-16 text-gray-400" />
          </div>
          
          <!-- Text -->
          <h2 class="text-2xl font-bold text-gray-800 mb-2">No Training Materials Yet</h2>
          <p class="text-gray-500 max-w-md mx-auto text-sm leading-relaxed mb-8">
            It looks like there are no training materials available for your organization at the moment. Please check back later or contact your administrator for more information.
          </p>

        </div>
        

        <!-- <div v-else class="mb-12 mt-4 p-6 bg-white rounded-lg shadow-sm border border-dashed border-gray-300 text-center">
          <h3 class="text-lg font-bold text-gray-500 mb-2">Organization Materials</h3>
          <p class="text-sm text-gray-400">
            Checking for documents under Org ID: <strong class="text-primary">{{ currentOrgId || 'Waiting for ID...' }}</strong>
          </p>
          <p v-if="orgInsights.length > 0 && filteredOrgInsights.length === 0" class="text-xs text-danger mt-2">
            (Data found, but hidden due to visibility filters for role: {{ activeProfileRole }})
          </p>
        </div> -->

        <!-- Presentations Section -->
        <div v-if="currentPresentations.length > 0" class="mb-12 mt-4 text-left">
          <div class="flex items-center justify-start mb-6">
            <h2 class="text-xl font-bold text-gray-800 px-2 border-l-4 border-primary ml-2">
              {{ $t("trainingLibrary.presentations") }}
            </h2>
            <span class="ml-3 bg-primary text-white text-xs font-bold px-3 py-1 rounded-full shadow-sm">
              {{ currentPresentations.length }}
            </span>
          </div>
          <carousel :per-page="4" :paginationEnabled="false" :navigationEnabled="true" class="px-2">
            <slide v-for="(item, index) in currentPresentations" :key="index" class="p-2">
              <div class="cursor-pointer group text-left bg-white p-3 rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-100 h-full flex flex-col" @click="presentationShow(item)">
                <div class="relative w-full rounded overflow-hidden bg-gray-100 flex items-center justify-center" style="aspect-ratio: 16/9;">
                  <!-- Static Image -->
                  <img v-if="item.image" :src="item.image" :title="item.title" class="w-full h-full object-contain block transition-transform duration-500 group-hover:scale-105" />
                  
                  <!-- Fallback for Custom Uploads -->
                  <div v-else class="w-full h-full flex flex-col items-center justify-center bg-primary-light">
                    <feather-icon icon="MonitorIcon" svgClasses="w-10 h-10 text-primary opacity-75 mb-2 transition-transform duration-500 group-hover:scale-110" />
                    <span class="text-[10px] font-bold text-primary uppercase tracking-wider">Presentation</span>
                  </div>

                  <div class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center pointer-events-none">
                    <feather-icon icon="PlayCircleIcon" class="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                </div>
                <p class="mt-4 font-bold text-gray-800 leading-tight group-hover:text-primary transition-colors line-clamp-2" :title="item.title">
                  {{ item.title }}
                </p>
              </div>
            </slide>
          </carousel>
          <vs-divider class="my-8" /> 
        </div>

        <!-- Videos Section -->
        <div v-if="currentVideos.length > 0" class="mb-12 text-left">
          <div class="flex items-center justify-start mb-6">
            <h2 class="text-xl font-bold text-gray-800 px-2 border-l-4 border-primary ml-2">
              {{ $t("trainingLibrary.video") }}
            </h2>
            <span class="ml-3 bg-primary text-white text-xs font-bold px-3 py-1 rounded-full shadow-sm">
              {{ currentVideos.length }}
            </span>
          </div>
          <carousel :per-page="4" :paginationEnabled="false" :navigationEnabled="true" class="px-2">
            <slide v-for="(item, index) in currentVideos" :key="index" class="p-2">
              <div class="cursor-pointer group text-left bg-white p-3 rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-100 h-full flex flex-col" @click="videoPlay(item)">
                <div class="relative w-full rounded overflow-hidden bg-gray-900 flex items-center justify-center" style="aspect-ratio: 16/9;">
                  <img v-if="item.image" :src="item.image" class="w-full h-full object-contain block opacity-90 transition-all duration-500 group-hover:scale-105 group-hover:opacity-100" />
                  <div v-else class="w-full h-full flex items-center justify-center bg-gray-800">
                    <feather-icon icon="VideoIcon" svgClasses="w-10 h-10 text-white opacity-50" />
                  </div>
                  <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
                     <div class="bg-white bg-opacity-95 rounded-full w-12 h-12 flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300">
                        <svg class="w-5 h-5 text-gray-800 fill-current ml-1" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                     </div>
                  </div>
                </div>
                <p class="mt-4 font-bold text-gray-800 leading-tight group-hover:text-primary transition-colors">
                  {{ item.title || 'Watch Video Tutorial' }}
                </p>
              </div>
            </slide>
          </carousel>
          <vs-divider class="my-8" />
        </div>

        <!-- Books Section -->
        <div v-if="currentBooks.length > 0" class="mb-8 text-left">
          <div class="flex items-center justify-start mb-6">
            <h2 class="text-xl font-bold text-gray-800 px-2 border-l-4 border-primary ml-2">
              {{ $t("trainingLibrary.books") }}
            </h2>
            <span class="ml-3 bg-primary text-white text-xs font-bold px-3 py-1 rounded-full shadow-sm">
              {{ currentBooks.length }} 
            </span>
          </div>
          <carousel :per-page="6" :paginationEnabled="false" :navigationEnabled="true" class="px-2">
            <slide v-for="(item, index) in currentBooks" :key="index" class="p-2">
              
              <!-- Changed to click handler so it properly handles your local /uploads/ files -->
              <div @click="openOrgMaterial(item.url)" class="block group h-full cursor-pointer">
                <div class="w-full h-full bg-white p-3 rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-100 flex flex-col items-center justify-center">
                  
                  <div class="w-full h-48 flex items-center justify-center overflow-hidden bg-gray-50 rounded">
                    <!-- If Static Book Image Exists -->
                    <img v-if="item.image" :src="item.image" class="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-105" />
                    
                    <!-- Fallback for Custom Upload Books (No Image) -->
                    <div v-else class="flex flex-col items-center justify-center text-center p-3 w-full h-full group-hover:bg-primary-light transition-colors duration-300">
                      <feather-icon icon="BookOpenIcon" svgClasses="w-10 h-10 text-primary mb-3 opacity-75 group-hover:scale-110 transition-transform duration-500" />
                      <span class="text-xs font-bold text-gray-700 line-clamp-3 leading-tight">{{ item.title }}</span>
                    </div>
                  </div>
                  
                  <p class="mt-4 w-full text-center font-bold text-gray-800 group-hover:text-primary transition-colors text-sm flex flex-col">
                    <span>View Book</span>
                    <span v-if="item.isCustom" class="text-[9px] text-gray-400 uppercase mt-1">Custom Upload</span>
                  </p>
                </div>
              </div>
              
            </slide>
          </carousel>
          <vs-divider class="my-8" />
        </div>
        <!-- Org Materials Section -->
        <div v-if="customDocuments.length > 0" class="mb-12 mt-4 text-left">
          <div class="flex items-center justify-start mb-6">
            <!-- Changed border and background to primary to match your theme -->
            <h2 class="text-xl font-bold text-gray-800 px-2 border-l-4 border-primary ml-2">
              Organization Materials
            </h2>
            <span class="ml-3 bg-primary text-white text-xs font-bold px-3 py-1 rounded-full shadow-sm">
              {{ customDocuments.length }}
            </span>
            <span class="ml-3 text-xs text-gray-400 font-medium tracking-wide uppercase">Custom Uploads</span>
          </div>
          
          <carousel :per-page="4" :paginationEnabled="false" :navigationEnabled="true" class="px-2">
            <!-- REPLACED filteredOrgInsights WITH customDocuments -->
            <slide v-for="(item, index) in customDocuments" :key="index" class="p-2">
              <div 
                class="cursor-pointer group text-left bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-100 h-full flex flex-col overflow-hidden" 
                @click="openOrgMaterial(item.file_url)"
              >
                <!-- Clean Icon Header (Mimics your Insights tab exactly) -->
                <div class="relative w-full flex items-center justify-center bg-gray-50 border-b border-gray-100 py-8">
                  
                  <!-- COMPANY NAME TAG (Replaces ORG DATA) -->
                  <div class="absolute top-3 left-3 bg-primary text-white text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider shadow-sm z-10 line-clamp-1 max-w-[100px]">
                    {{ companyName || 'Company' }}
                  </div>
                  
                  <!-- CATEGORY TAG -->
                  <div class="absolute top-3 right-3 text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider bg-white border border-gray-200 text-gray-500 z-10">
                    {{ item.category || 'Document' }}
                  </div>

                  <!-- Clean Document Icon -->
                  <feather-icon :icon="getFileIcon(item.file_type)" :svgClasses="['w-16 h-16 transition-transform duration-500 group-hover:scale-110', getFileColor(item.file_type)]" />
                  
                </div>
                
                <!-- Text Content -->
                <div class="p-4 flex-grow flex flex-col bg-white">
                  <h3 class="font-bold text-gray-800 leading-tight group-hover:text-primary transition-colors line-clamp-1 mb-1" :title="item.title">
                    {{ item.title }}
                  </h3>
                  <p class="text-xs text-gray-500 line-clamp-2 mt-1">
                    {{ item.description || 'Organization specific resource.' }}
                  </p>
                  
                  <div class="mt-auto pt-3 flex items-center justify-between text-xs font-semibold text-gray-400">
                    <span>{{ item.file_size || 'Unknown Size' }}</span>
                    <span class="flex items-center group-hover:text-primary transition-colors">
                      Open <feather-icon :icon="item.file_type === 'link' ? 'ExternalLinkIcon' : 'DownloadIcon'" svgClasses="w-3 h-3 ml-1" />
                    </span>
                  </div>
                </div>
              </div>
            </slide>
          </carousel>
          
        </div>

      </vx-card>

      <!-- Bottom Action Cards -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
        <a href="mailto:admin@getboarded.com" class="flex items-center justify-start p-6 bg-white rounded-md shadow-sm hover:shadow-md transition-shadow border border-gray-200 group cursor-pointer text-left">
          <div class="mr-5">
            <img src="@/assets/images/slider/player_area/life-buoy_1.svg" alt="Contact" class="w-7 h-7 opacity-50 group-hover:opacity-80 transition-opacity" />
          </div>
          <div>
            <h3 class="text-base font-bold text-gray-800">{{ $t("trainingLibrary.contact") }}</h3>
            <p class="text-xs text-gray-500 mt-1 group-hover:text-primary transition-colors font-medium">Get help from GetBoarded Support</p>
          </div>
        </a>
        <a :href="`https://getboarded.com/organization/${companyName ? companyName.toLowerCase() : ''}/marketplace`" target="_blank" class="flex items-center justify-start p-6 bg-white rounded-md shadow-sm hover:shadow-md transition-shadow border border-gray-200 group cursor-pointer text-left">
          <div class="mr-5">
            <img src="@/assets/images/slider/player_area/box_1.svg" alt="Apps" class="w-7 h-7 opacity-50 group-hover:opacity-80 transition-opacity" />
          </div>
          <div>
            <h3 class="text-base font-bold text-gray-800">{{ $t("trainingLibrary.appsIntegration") }}</h3>
            <p class="text-xs text-gray-500 mt-1 group-hover:text-primary transition-colors font-medium">Explore features and tools</p>
          </div>
        </a>
      </div>

    </div>
    
  </div>
</template>

<script>
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { Carousel, Slide } from "vue-carousel";
import VueSlickCarousel from "vue-slick-carousel";
import "vue-slick-carousel/dist/vue-slick-carousel.css";
// optional style for arrows & dots
import "vue-slick-carousel/dist/vue-slick-carousel-theme.css";
import axios from "axios";
export default {
  props: {
    footerType: { type: String, required: true },
    hideScrollToTop: { type: Boolean, required: true },
    navbarType: { type: String, required: true },
    navbarColor: { type: String, required: true, default: "#fff" },
    routerTransition: { type: String, required: true }
  },
  data() {
    return {
      active: false,
      psrc: "",
      vsrc: "",
      companyName: "",
      orgInsights: [],
      traineePresentations: [
        // {
        //   image: require("@/assets/images/slider/slide_p1.png"),
        //   url:
        //       "https://www.scribd.com/embeds/591457967/content?start_page=1&view_mode=slideshow&access_key=key-8v6Moi7J2s77EMh12Mn7",
        //   title: "Coaching and Mentoring as levers of conscious leadership",
        //   description:
        //       "Growing your self-awareness and unique leadership style. Coaching and Mentoring are two arts of leadership that are built on core talents of listening, empathy and speaking with impact.\n" +
        //       "\n" +
        //       "Identifying different situations and types in the team and learning how to decode them to maximize the impact of your communication.\n" +
        //       "\n" +
        //       "Exploring coaching and mentoring techniques that fit your personal style and drive better relationships and results."
        // },
        // {
        //   image: require("@/assets/images/slider/slide_p2.png"),
        //   url:
        //       "https://www.scribd.com/embeds/591457969/content?start_page=1&view_mode=slideshow&access_key=key-eT27oXMOXGKy8Wd1QSHq",
        //   title: "Your Role as a Mentor and a Coach",
        //   description:
        //       "In a forest, there is no master tree that plans and dictates change when rain fails to fall or when the spring comes early. The whole ecosystem reacts creatively, in the moment.” It’s more than behaviour, It is a systemic approach of dynamic collaboration."
        // },
        // {
        //   image: require("@/assets/images/slider/slide_p3.png"),
        //   url:
        //       "https://www.scribd.com/embeds/594558973/content?start_page=1&view_mode=slideshow&access_key=key-xiOWD6tKxvu6QB8md1bQ",
        //   title: "Developing a Culture of Sustainable Performance",
        //   description:
        //       "Sustainable performance is driven by constent and constant action.  It is a continual, holistic process, as opposed to those based on a traditional, one-off annual appraisal. It enables our organization to create a trusted environment in which everyone feels empowered to take control of their own development."
        // },
        // {
        //   image: require("@/assets/images/slider/slide_p4.png"),
        //   url:
        //       "https://www.scribd.com/embeds/594560773/content?start_page=1&view_mode=slideshow&access_key=key-LMj7ahbWT5HzMnLrx68T",
        //   title: "Setting Up Org",
        //   description:
        //       "Question everything,” Albert Einstein famously said. Personal creativity and organizational innovation rely on a willingness to seek out novel information. Asking the right questions can unlock opportunities you didn't even realize existed and take your career to new heights. Configuring the surveys, nudges and relationship feedbacks as per the organizational ethos is essntial to make the relationship strong and the program effective."
        // },
        // {
        //   image: require("@/assets/images/slider/slide_p5.png"),
        //   url:
        //       "https://www.scribd.com/embeds/594576409/content?start_page=1&view_mode=slideshow&access_key=key-g1vf8l0tOa78Vaw2fUDR",
        //   title: "Player Goal Setting",
        //   description:
        //       "Goal setting is a purposeful and explicit process that starts with identifying a new objective, skill, or project you want to achieve. The first steps to goal setting is to plan for a BIG, SMART and WORTHY achievement that you can envision.\n" +
        //       "\n"
        // },
        // {
        //   image: require("@/assets/images/slider/slide_p6.png"),
        //   url:
        //       "https://www.scribd.com/embeds/594582732/content?start_page=1&view_mode=slideshow&access_key=key-ELulDUrudl6kE6SueFUq",
        //   title:
        //       "Mentor Training on Product and Methodology to Set First Goal with Player",
        //   description:
        //       "A handbook to use the tool to set a proper goal, and manage the progression towards its achievement. How to monitor the development progress and resolve issues that may get in the way of the player reaching their goals."
        // },
        // {
        //   image: require("@/assets/images/slider/slide_p7.png"),
        //   url:
        //       "https://www.scribd.com/embeds/594584727/content?start_page=1&view_mode=slideshow&access_key=key-75eEz3UslXIC33wbyOXz",
        //   title: "Achieving BIG goals with Small Habits",
        //   description:
        //       "SET: How can we set good goals that align with our vision and mission?\n" +
        //       "\n" +
        //       " \n" +
        //       "\n" +
        //       "STICK: Build support network.\n" +
        //       "\n" +
        //       " \n" +
        //       "\n" +
        //       "SUSTAIN: Create meaningful reminders to nudge you into small habits.\n" +
        //       "\n" +
        //       "\n" +
        //       "SCORE: Celebrate small wins all along the way, Aim high with a “What if” and “Why not” mindset. Set your eyes up and hold your head high for you ‘Do’ or ‘Do not’, there is no Try."
        // },
        // {
        //   image: require("@/assets/images/slider/slide_p8.png"),
        //   url:
        //       "https://www.scribd.com/embeds/594589530/content?start_page=1&view_mode=slideshow&access_key=key-Yo2FuO2QAZ2hEbJ68FgE",
        //   title:
        //       "Monitoring Goals and Timing Interventions in Dynamic Environments",
        //   description:
        //       "There is a catch to all of the goal setting paradigms we discussed. It is based on two assumptions: The person has the ‘potential’ (ability) to achieve the set goal He will have the resources (environment) to achieve it This handbook describes how to manage the process and predict deviations on goals."
        // }
      ],
      traineeVideos: [
        {image: "https://app.decast.live/img/logot.39d4903f.svg",
          url: "https://app.decast.live/events/pmf-workshop-2026-09-04"},
        {
          image: "https://img.youtube.com/vi/TiR2vVPF8eI/hqdefault.jpg",
          url: "https://www.youtube.com/embed/TiR2vVPF8eI"
        },
        {
          image: "https://img.youtube.com/vi/xHWKiB6hIeg/hqdefault.jpg",
          url: "https://www.youtube.com/embed/xHWKiB6hIeg"
        },
        {
          image: "https://img.youtube.com/vi/5y73vSsMUBU/hqdefault.jpg",
          url: "https://www.youtube.com/embed/5y73vSsMUBU"
        },
        {
          image: "https://img.youtube.com/vi/E5Qv3daS-l0/hqdefault.jpg",
          url: "https://www.youtube.com/embed/E5Qv3daS-l0"
        },
        {
          image: "https://img.youtube.com/vi/kLvTat8I-iY/hqdefault.jpg",
          url: "https://www.youtube.com/embed/kLvTat8I-iY"
        },
        {
          image: "https://img.youtube.com/vi/M5GQj8hiWrA/hqdefault.jpg",
          url: "https://www.youtube.com/embed/M5GQj8hiWrA"
        },
        
        // {
        //   image: require("@/assets/images/slider/slide_v2.png"),
        //   url: "https://www.youtube.com/embed/aHDvEfiSipo"
        // },

      ],
      coachPresentations: [
        // {
        //   image: require("@/assets/images/slider/datarmony/presentation/p1.jpg"),
        //   url:
        //       "https://www.scribd.com/embeds/599077391/content?start_page=1&view_mode=slideshow&access_key=key-izTrlgBvI5G7kYOFtIvz",
        //   title: "Your Role as a Mentor and a Coach",
        //   description:
        //       "In a forest, there is no master tree that plans and dictates change when rain fails to fall or when the spring comes early. The whole ecosystem reacts creatively, in the moment.” It’s more than behaviour, It is a systemic approach of dynamic collaboration"
        // },
        // {
        //   image: require("@/assets/images/slider/datarmony/presentation/p2.jpg"),
        //   url:
        //       "https://www.scribd.com/embeds/599077392/content?start_page=1&view_mode=slideshow&access_key=key-4enF7u5XHaDY7Vigjift",
        //   title: "Developing a Culture of Sustainable Performance",
        //   description:
        //       "Sustainable performance is driven by constant and constant action.  It is a continual, holistic process, as opposed to those based on a traditional, one-off annual appraisal. It enables our organization to create a trusted environment in which everyone feels empowered to take control of their own development."
        // },
        // {
        //   image: require("@/assets/images/slider/datarmony/presentation/p3.jpg"),
        //   url:
        //       "https://www.scribd.com/embeds/599077393/content?start_page=1&view_mode=slideshow&access_key=key-eZYHLG6Pjn6HTUuPZd8K",
        //   title: "Setting Up Org",
        //   description:
        //       "Question everything,” Albert Einstein famously said. Personal creativity and organizational innovation rely on a willingness to seek out novel information. Asking the right questions can unlock opportunities you didn't even realize existed and take your career to new heights. Configuring the surveys, nudges and relationship feedbacks as per the organizational ethos is essential to make the relationship strong and the program effective"
        // },
        // {
        //   image: require("@/assets/images/slider/datarmony/presentation/p4.jpg"),
        //   url:
        //       "https://www.scribd.com/embeds/599077389/content?start_page=1&view_mode=slideshow&access_key=key-JBfkbhrRB5baKLyIdtEa",
        //   title:
        //       "Mentor Training on Product and Methodology to Set First Goal with Player",
        //   description:
        //       "Question everything,” Albert Einstein famously said. Personal creativity and organizational innovation rely on a willingness to seek out novel information. Asking the right questions can unlock opportunities you didn't even realize existed and take your career to new heights. Configuring the surveys, nudges and relationship feedbacks as per the organizational ethos is essntial to make the relationship strong and the program effective."
        // },
        // {
        //   image: require("@/assets/images/slider/datarmony/presentation/p1.jpg"),
        //   url:
        //       "https://www.scribd.com/embeds/599077391/content?start_page=1&view_mode=slideshow&access_key=key-izTrlgBvI5G7kYOFtIvz",
        //   title: "Your Role as a Mentor and a Coach",
        //   description:
        //       "In a forest, there is no master tree that plans and dictates change when rain fails to fall or when the spring comes early. The whole ecosystem reacts creatively, in the moment.” It’s more than behaviour, It is a systemic approach of dynamic collaboration"
        // },
        // {
        //   image: require("@/assets/images/slider/datarmony/presentation/p2.jpg"),
        //   url:
        //       "https://www.scribd.com/embeds/599077392/content?start_page=1&view_mode=slideshow&access_key=key-4enF7u5XHaDY7Vigjift",
        //   title: "Developing a Culture of Sustainable Performance",
        //   description:
        //       "Sustainable performance is driven by constant and constant action.  It is a continual, holistic process, as opposed to those based on a traditional, one-off annual appraisal. It enables our organization to create a trusted environment in which everyone feels empowered to take control of their own development."
        // },
      ],
      coachVideos: [
        // {
        //   image: require("@/assets/images/slider/slide_v1.png"),
        //   url: "https://www.youtube.com/embed/75d_29QWELk"
        // },
        // {
        //   image: require("@/assets/images/slider/slide_v2.png"),
        //   url: "https://www.youtube.com/embed/aHDvEfiSipo"
        // },
        // {
        //   image: require("@/assets/images/slider/slide_v3.jpg"),
        //   url: "https://www.youtube.com/embed/M759yNSOTMs"
        // },
        // {
        //   image: require("@/assets/images/slider/slide_v4.jpg"),
        //   url: "https://www.youtube.com/embed/rK1mhvv920s"
        // },
        // {
        //   image: require("@/assets/images/slider/slide_v5.jpg"),
        //   url: "https://www.youtube.com/embed/-8BjkOo_Ts4"
        // },
        // {
        //   image: require("@/assets/images/slider/slide_v1.png"),
        //   url: "https://www.youtube.com/embed/75d_29QWELk"
        // },
        // {
        //   image: require("@/assets/images/slider/slide_v2.png"),
        //   url: "https://www.youtube.com/embed/aHDvEfiSipo"
        // },
      ],
      traineeBooks: [
        { image: require("@/assets/images/slider/powerOfHabit.png"),   url: 'https://www.google.com/search?q=the+power+of+habit+book'   },
        { image: require("@/assets/images/slider/miracle.png"),  url: 'https://www.google.com/search?q=the+miracle+morning'  },
        { image: require("@/assets/images/slider/mindSet.png"),  url: 'https://www.google.com/search?q=mindset%3A+the+new+psychology+of+success'  },
        { image: require("@/assets/images/slider/5_second_rule.png"),   url: 'https://www.google.com/search?q=5+second+rule+mel+robbins+book'   },
        { image: require("@/assets/images/slider/deep_work.png"),  url: 'https://www.google.com/search?q=deep+work'  },
        { image: require("@/assets/images/slider/the_execuition.png"),  url: 'https://www.google.com/search?q=Execution%3A+The+Discipline+of+Getting+Things+Done'  },
        { image: require("@/assets/images/slider/the_infinte_game.png"),  url: 'https://google.com/search?q=the+infinite+game'  },
        { image: require("@/assets/images/slider/the_one_thing.png"),  url: 'https://www.google.com/search?q=the+one+thing'  },
        { image: require("@/assets/images/slider/thinking_fast_and_slow.png"),  url: 'https://www.google.com/search?q=the+international+bestseller+thinking+fast+and+slow'  },
        { image: require("@/assets/images/slider/a_thousand_brains.png"),  url: 'https://www.google.com/search?q=+A+THOUSAND+BRAINS'  },
        { image: require("@/assets/images/slider/think_again.png"),  url: 'https://www.google.com/search?q=Think+AGAIN+Adam+Grant'  },
        { image: require("@/assets/images/slider/power_of_subconscious.png"),  url: 'https://www.google.com/search?q=the+power+of+your+subconscious+mind'  },
        { image: require("@/assets/images/slider/the_compoud_effect.png"),  url: 'https://www.google.com/search?q=the+compound+effect'  },
        { image: require("@/assets/images/slider/the_ellimal_egos.png"),  url: 'https://www.google.com/search?q=Allemaal+EGOS'  },
        { image: require("@/assets/images/slider/unlocking_leadership.png"),  url: 'https://www.google.com/search?q=Unlocking+leadership'  },
        { image: require("@/assets/images/slider/id_in_doubt_wash_your_hair.jpg"),  url: 'https://www.google.com/search?q=If+In+Doubt%2C+Wash+Your+Hair'  },
        { image: require("@/assets/images/slider/why_has_nobody_told_me.jpg"),  url: 'https://www.google.com/search?q=Why+Has+Nobody+Told+Me+This+Before'  },
        { image: require("@/assets/images/slider/get_your_shot_together.jpg"),  url: 'https://www.google.com/search?q=Get+Your+Sh*t+Together'  },
        { image: require("@/assets/images/slider/speak_the_truth.jpg"),  url: 'https://www.google.com/search?q=Speak+Your+Truth+book'  },
        { image: require("@/assets/images/slider/you_are_a_badass.jpg"),  url: 'https://www.google.com/search?q=You+Are+A+Badass'  },
        { image: require("@/assets/images/slider/good_vibes_good_life.jpg"),  url: 'https://www.google.com/search?q=Good+Vibes%2C+Good+Life'  },
        { image: require("@/assets/images/slider/the_5am_club.jpg"),  url: 'https://www.google.com/search?q=The+5AM+Club'  },
        { image: require("@/assets/images/slider/manifest.jpg"),  url: 'https://www.google.com/search?rlz=1C1RXQR_enIN987IN987&sxsrf=AJOqlzXRuv5l-DvaOgvV2TZs74Q7HBMxSg:1674552959249&q=MANIFEST+book'  },
        { image: require("@/assets/images/slider/atomic_habbit.jpg"),  url: 'https://www.google.com/search?q=Atomic+Habits%3A+An+Easy+%26+Proven+Way+to+Build+Good+Habits'  },
        { image: require("@/assets/images/slider/big_magic.jpg"),  url: 'https://www.google.com/search?q=Big+Magic%3A+Creative+Living+Beyond+Fear'  },
        { image: require("@/assets/images/slider/comfort_book.jpg"),  url: 'https://www.google.com/search?q=The+Comfort+Book'  },
        { image: require("@/assets/images/slider/the_book_you_wish_your_parent.jpg"),  url: 'https://www.google.com/search?q=The+Book+You+Wish+Your+Parents'  },
        { image: require("@/assets/images/slider/how_to_win_friends_and_people.jpg"),  url: 'https://www.google.com/search?q=How+To+Win+Friends+And+Influence+People'  },
        { image: require("@/assets/images/slider/the_life_changing.jpg"),  url: 'https://www.google.com/search?q=The+Life+Changing+Magic+Of+Tidying+Up'  },
        { image: require("@/assets/images/slider/toxic_positivity.jpg"),  url: 'https://www.google.com/search?q=Toxic+Positivity%3A+Keeping+It+Real+in+a+World+Obsessed+with+Being+Happy'  },
        { image: require("@/assets/images/slider/financial_wellness.jpg"),  url: 'https://www.google.com/search?q=Financial+Wellness+And+How+To+Find+It'  },
        { image: require("@/assets/images/slider/out_of_office.jpg"),  url: 'https://www.google.com/search?q=Out+Of+Office%3A+The+Big+Problem+And+Bigger+Promise+Of+Working+From+Home'  },
        { image: require("@/assets/images/slider/actionable_gammification.jpg"),  url: 'https://www.google.com/search?q=Actionable+Gamification+books'  },
        { image: require("@/assets/images/slider/how_to_build_habbit_forming.jpg"),  url: 'https://www.google.com/search?q=How+to+Build+Habit-Forming+Products'  },
        { image: require("@/assets/images/slider/testing_bussiness_ideas.jpg"),  url: 'https://www.google.com/search?rlz=1C1RXQR_enIN987IN987&sxsrf=AJOqlzVGrqlV9hWcW2YNjhIgUNwjYXREyA:1674555684066&q=Testing+Bussiness+ideas'  },
        { image: require("@/assets/images/slider/how_to_read_paper.jpg"),  url: 'https://www.google.com/search?q=How+to+Read+a+Paper+book&spell'  },
        { image: require("@/assets/images/slider/this_is_service_design_doing.jpg"),  url: 'https://www.google.com/search?q=this+is+service+design+doing'  },
        { image: require("@/assets/images/slider/continous_discovery_habbits.jpg"),  url: 'https://www.google.com/search?q=continuous+discovery+habits'  },
        { image: require("@/assets/images/slider/the_service_innovation_handbook.jpg"),  url: 'https://www.google.com/search?q=the+service+innovation+handbook'  },
        { image: require("@/assets/images/slider/thinking_in_system.jpg"),  url: 'https://www.google.com/search?q=thinking+in+systems'  },
        { image: require("@/assets/images/slider/the_incible_company.jpg"),  url: 'https://www.google.com/search?q=the+invincible+company'  },
        { image: require("@/assets/images/slider/winning_at_new_product.jpg"),  url: 'https://www.google.com/search?q=winning+at+new+products'  },
        { image: require("@/assets/images/slider/the_expeirince_centric.jpg"),  url: 'https://www.google.com/search?q=the+experience+centric+organization'  },
        { image: require("@/assets/images/slider/the_behavior_change_wheel.jpg"),  url: 'https://www.google.com/search?q=the+behaviour+change+wheel+amazon'  },
        { image: require("@/assets/images/slider/design_people_how_think.jpg"),  url: 'https://www.google.com/search?q=design+for+how+people+think'  },
        { image: require("@/assets/images/slider/desiging_for_behavior_change.jpg"),  url: 'https://www.google.com/search?q=designing+for+behavior+change'  },
        { image: require("@/assets/images/slider/start_at_the_end.jpg"),  url: 'https://www.google.com/search?q=Start+At+The+End'  },
        { image: require("@/assets/images/slider/the_behavior_bussiness.jpg"),  url: 'https://www.google.com/search?q=the+behavior+business'  },
        { image: require("@/assets/images/slider/behavior_insight.jpg"),  url: 'https://www.google.com/search?q=the+behavioural+insights+book'  },
        { image: require("@/assets/images/slider/designing_for_behaviour_change.jpg"),  url: 'https://www.google.com/search?q=Designing+for+Behavior+Change'  },
        { image: require("@/assets/images/slider/ripple.jpg"),  url: 'https://www.google.com/search?q=Ripple+the+big+effects'  },
        { image: require("@/assets/images/slider/applying_behavior_change_toprivate_sector.jpg"),  url: 'https://www.google.com/search?q=applying+behavioural+science+to+the+private+sector'  },
        { image: require("@/assets/images/slider/winning_at_new_product.jpg"),  url: 'https://www.google.com/search?q=winning+at+new+products'  },
        { image: require("@/assets/images/slider/the_expeirince_centric.jpg"),  url: 'https://www.google.com/search?q=the+experience+centric+organization'  },
        { image: require("@/assets/images/slider/the_behavior_change_wheel.jpg"),  url: 'https://www.google.com/search?q=the+behaviour+change+wheel+amazon'  },
        { image: require("@/assets/images/slider/design_people_how_think.jpg"),  url: 'https://www.google.com/search?q=design+for+how+people+think'  },
        { image: require("@/assets/images/slider/desiging_for_behavior_change.jpg"),  url: 'https://www.google.com/search?q=designing+for+behavior+change'  },
        { image: require("@/assets/images/slider/start_at_the_end.jpg"),  url: 'https://www.google.com/search?q=Start+At+The+End'  },
        { image: require("@/assets/images/slider/the_behavior_bussiness.jpg"),  url: 'https://www.google.com/search?q=the+behavior+business'  },
        { image: require("@/assets/images/slider/behavior_insight.jpg"),  url: 'https://www.google.com/search?q=the+behavioural+insights+book'  },
        { image: require("@/assets/images/slider/designing_for_behaviour_change.jpg"),  url: 'https://www.google.com/search?q=Designing+for+Behavior+Change'  },
        { image: require("@/assets/images/slider/ripple.jpg"),  url: 'https://www.google.com/search?q=Ripple+the+big+effects'  },
        { image: require("@/assets/images/slider/applying_behavior_change_toprivate_sector.jpg"),  url: 'https://www.google.com/search?q=applying+behavioural+science+to+the+private+sector'  },
        { image: require("@/assets/images/slider/winning_at_new_product.jpg"),  url: 'https://www.google.com/search?q=winning+at+new+products'  },
        { image: require("@/assets/images/slider/the_expeirince_centric.jpg"),  url: 'https://www.google.com/search?q=the+experience+centric+organization'  },
        { image: require("@/assets/images/slider/the_behavior_change_wheel.jpg"),  url: 'https://www.google.com/search?q=the+behaviour+change+wheel+amazon'  },
        { image: require("@/assets/images/slider/design_people_how_think.jpg"),  url: 'https://www.google.com/search?q=design+for+how+people+think'  },
        { image: require("@/assets/images/slider/desiging_for_behavior_change.jpg"),  url: 'https://www.google.com/search?q=designing+for+behavior+change'  },
        { image: require("@/assets/images/slider/start_at_the_end.jpg"),  url: 'https://www.google.com/search?q=Start+At+The+End'  },
        { image: require("@/assets/images/slider/the_behavior_bussiness.jpg"),  url: 'https://www.google.com/search?q=the+behavior+business'  },
        { image: require("@/assets/images/slider/behavior_insight.jpg"),  url: 'https://www.google.com/search?q=the+behavioural+insights+book'  },
        { image: require("@/assets/images/slider/designing_for_behaviour_change.jpg"),  url: 'https://www.google.com/search?q=Designing+for+Behavior+Change'  },
        { image: require("@/assets/images/slider/ripple.jpg"),  url: 'https://www.google.com/search?q=Ripple+the+big+effects'  },
        { image: require("@/assets/images/slider/applying_behavior_change_toprivate_sector.jpg"),  url: 'https://www.google.com/search?q=applying+behavioural+science+to+the+private+sector'  },
        { image: require("@/assets/images/slider/ripple.jpg"),  url: 'https://www.google.com/search?q=Ripple+the+big+effects'  },
        { image: require("@/assets/images/slider/applying_behavior_change_toprivate_sector.jpg"),  url: 'https://www.google.com/search?q=applying+behavioural+science+to+the+private+sector'  },

      ],
      coachBooks: [],
      customPrimaryColor: "#3DC9B3",
      customNavbarColor: "#3DC9B3",
      routerTransitionsList: [
        { text: "Zoom Fade", value: "zoom-fade" },
        { text: "Slide Fade", value: "slide-fade" },
        { text: "Fade Bottom", value: "fade-bottom" },
        { text: "Fade", value: "fade" },
        { text: "Zoom Out", value: "zoom-out" },
        { text: "None", value: "none" }
      ],
      flag: false,
      flag1: false,
      settings: {
        maxScrollbarLength: 60,
        wheelSpeed: 0.6
      },
      themeColors: ["#9DCD5B", "#28C76F", "#EA5455", "#FF9F43", "#1E1E1E"]
    };
  },
  watch: {
    layoutType(val) {
      if (val === "horizontal") {
        if (this.themeMode === "semi-dark") this.themeMode = "light";
        if (this.navbarType === "hidden") this.navbarTypeLocal = "floating";
        this.$emit("updateNavbarColor", "#fff");
      }
    },
    // ==========================================
    // THE FIX: WATCH FOR THE ID TO BECOME READY!
    // ==========================================
    currentOrgId: {
      immediate: true, // Run this immediately on page load
      handler(newId) {
        if (newId) {
          this.fetchOrgInsights();
        }
      }
    }
  },
  
  computed: {
    customDocuments() {
      return this.filteredOrgInsights
        .filter(doc => !['Presentation', 'Video', 'Book'].includes(doc.category))
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    },

    isTrainingCenterEmpty() {
      return (
        this.customDocuments.length === 0 &&
        this.currentPresentations.length === 0 &&
        this.currentVideos.length === 0 &&
        this.currentBooks.length === 0
      );
    },

    currentOrgId() {
      // 1. Check Cookies (Prioritizing companyId over Mongo to match Admin uploads)
      if (window.$cookies) {
        const cookieOrg = window.$cookies.get('companyId') || window.$cookies.get('organizationId') || window.$cookies.get('mongo_org_id');
        if (cookieOrg) return cookieOrg;
      }
      
      // 2. Check LocalStorage
      const localOrg = localStorage.getItem('companyId') || localStorage.getItem('organizationId') || localStorage.getItem('orgId') || localStorage.getItem('mongo_org_id');
      if (localOrg && localOrg !== 'undefined' && localOrg !== 'null') return localOrg;
      
      // 3. Dig into userInfo
      try {
        const userInfo = JSON.parse(localStorage.getItem('userInfo'));
        if (userInfo) {
          if (userInfo.companyId) return userInfo.companyId;
          if (userInfo.organizationId) return userInfo.organizationId;
          if (userInfo.company && userInfo.company.id) return userInfo.company.id;
          if (userInfo.mongo_org_id) return userInfo.mongo_org_id;
          if (userInfo.company && userInfo.company.mongo_org_id) return userInfo.company.mongo_org_id;
        }
      } catch (e) {}

      // 4. Check Vuex
      if (this.$store && this.$store.state) {
        if (this.$store.state.userManagement && this.$store.state.userManagement.company) {
          if (this.$store.state.userManagement.company.id) return this.$store.state.userManagement.company.id;
          if (this.$store.state.userManagement.company.mongo_org_id) return this.$store.state.userManagement.company.mongo_org_id;
        }
        if (this.$store.state.AppActiveUser) {
          if (this.$store.state.AppActiveUser.companyId) return this.$store.state.AppActiveUser.companyId;
          if (this.$store.state.AppActiveUser.organizationId) return this.$store.state.AppActiveUser.organizationId;
          if (this.$store.state.AppActiveUser.mongo_org_id) return this.$store.state.AppActiveUser.mongo_org_id;
        }
      }
      return this.$route.query.company_id || this.$route.query.org_id || ""; 
    },
    activeProfileRole() {
      try {
        const userInfo = JSON.parse(localStorage.getItem("userInfo"));
        if (userInfo) {
          const role = userInfo.userType || userInfo.user_type || userInfo.role;
          if (role) return role.toLowerCase();
        }
        return "coach"; // Fallback
      } catch (e) {
        return "coach";
      }
    },

    // STRICT VISIBILITY FILTERING
    filteredOrgInsights() {
      const role = this.activeProfileRole;
      return this.orgInsights.filter(doc => {
        if (doc.visibility === 'All Users') return true;
        // Fix: Role from db is 'trainee', so this now matches perfectly!
        if (doc.visibility === 'Founders' && role === 'trainee') return true;
        if (doc.visibility === 'Coaches' && role === 'coach') return true;
        return false;
      });
    },

    customPresentations() {
      return this.filteredOrgInsights
        .filter(doc => doc.category === 'Presentation')
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
        .map(doc => ({ ...doc, url: doc.file_url, isCustom: true }));
    },
    
    customVideos() {
      return this.filteredOrgInsights
        .filter(doc => doc.category === 'Video')
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
        .map(doc => ({ ...doc, url: doc.file_url, isCustom: true }));
    },
    
    customBooks() {
      return this.filteredOrgInsights
        .filter(doc => doc.category === 'Book')
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
        .map(doc => ({ ...doc, url: doc.file_url, isCustom: true }));
    },

    // 3. Merge Arrays: Put CUSTOM UPLOADS FIRST, then STATIC DEFAULT LISTS
    currentPresentations() {
      const list = (this.activeProfileRole === 'trainee') ? this.traineePresentations : this.coachPresentations;
      return [...this.customPresentations, ...(list || [])];
    },
    
    currentVideos() {
      const list = (this.activeProfileRole === 'trainee') ? this.traineeVideos : this.coachVideos;
      return [...this.customVideos, ...(list || [])];
    },
    
    currentBooks() {
      const list = (this.activeProfileRole === 'trainee') ? this.traineeBooks : this.coachBooks;
      return [...this.customBooks, ...(list || [])];
    },
    footerTypeLocal: {
      get() {
        return this.footerType;
      },
      set(val) {
        this.$emit("updateFooter", val);
      }
    },
    hideScrollToTopLocal: {
      get() {
        return this.hideScrollToTop;
      },
      set(val) {
        this.$emit("toggleHideScrollToTop", val);
      }
    },
    navbarColorInitial() {
      return this.$store.state.theme === "dark" ? "#10163a" : "#fff";
    },
    navbarColorOptionClasses() {
      return color => {
        const classes = {};
        if (color === this.navbarColorLocal) classes["shadow-outline"] = true;
        if (this.navbarTypeLocal === "static")
          classes["cursor-not-allowed"] = true;
        return classes;
      };
    },
    navbarColorLocal: {
      get() {
        return this.navbarColor;
      },
      set(val) {
        if (this.navbarType === "static") return;
        this.$emit("updateNavbarColor", val);
      }
    },
    navbarTypeLocal: {
      get() {
        return this.navbarType;
      },
      set(val) {
        this.$emit("updateNavbar", val);
      }
    },
    layoutType: {
      get() {
        return this.$store.state.mainLayoutType;
      },
      set(val) {
        this.$store.commit("UPDATE_MAIN_LAYOUT_TYPE", val);
      }
    },
    primaryColor: {
      get() {
        return this.$store.state.themePrimaryColor;
      },
      set(val) {
        this.$store.commit("UPDATE_PRIMARY_COLOR", val);
      }
    },
    reduced_sidebar: {
      get() {
        return this.$store.state.reduceButton;
      },
      set(val) {
        this.$store.commit("TOGGLE_REDUCE_BUTTON", val);
      }
    },
    routerTransitionLocal: {
      get() {
        return this.routerTransition;
      },
      set(val) {
        this.$emit("updateRouterTransition", val);
      }
    },
    rtl: {
      get() {
        return this.$vs.rtl;
      },
      set(val) {
        this.$vs.rtl = val;
      }
    },
    themeMode: {
      get() {
        return this.$store.state.theme;
      },
      set(val) {
        this.$store.dispatch("updateTheme", val);
      }
    },
    scrollbarTag() {
      return this.$store.state.is_touch_device ? "div" : "VuePerfectScrollbar";
    },
    windowWidth() {
      return this.$store.state.windowWidth;
    },
  },
  methods: {
    getResolvedOrgId() {
      if (window.$cookies) {
        const cookieOrg = window.$cookies.get('companyId') || window.$cookies.get('organizationId') || window.$cookies.get('mongo_org_id');
        if (cookieOrg) return cookieOrg;
      }
      
      const localOrg = localStorage.getItem('companyId') || localStorage.getItem('organizationId') || localStorage.getItem('orgId') || localStorage.getItem('mongo_org_id');
      if (localOrg && localOrg !== 'undefined' && localOrg !== 'null') return localOrg;
      
      try {
        const userInfo = JSON.parse(localStorage.getItem('userInfo'));
        if (userInfo) {
          if (userInfo.companyId) return userInfo.companyId;
          if (userInfo.organizationId) return userInfo.organizationId;
          if (userInfo.company && userInfo.company.id) return userInfo.company.id;
          if (userInfo.mongo_org_id) return userInfo.mongo_org_id;
          if (userInfo.company && userInfo.company.mongo_org_id) return userInfo.company.mongo_org_id;
        }
      } catch (e) {}

      if (this.$store && this.$store.state) {
        if (this.$store.state.userManagement && this.$store.state.userManagement.company) {
          if (this.$store.state.userManagement.company.id) return this.$store.state.userManagement.company.id;
          if (this.$store.state.userManagement.company.mongo_org_id) return this.$store.state.userManagement.company.mongo_org_id;
        }
        if (this.$store.state.AppActiveUser) {
          if (this.$store.state.AppActiveUser.companyId) return this.$store.state.AppActiveUser.companyId;
          if (this.$store.state.AppActiveUser.organizationId) return this.$store.state.AppActiveUser.organizationId;
          if (this.$store.state.AppActiveUser.mongo_org_id) return this.$store.state.AppActiveUser.mongo_org_id;
        }
      }
      return this.$route.query.company_id || this.$route.query.org_id || ""; 
    },
    async fetchOrgInsights() {
      try {
        // 1. Get the user's email from localStorage
        const userInfoStr = localStorage.getItem('userInfo');
        if (!userInfoStr) {
          console.warn("No user info found in local storage.");
          return;
        }

        const userInfo = JSON.parse(userInfoStr);
        const userEmail = userInfo.email;

        if (!userEmail) {
          console.warn("No email found in user info.");
          return;
        }

        // Get token for authorization
        const token = localStorage.getItem('accessToken') || localStorage.getItem('feathers-jwt') || '';
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        console.log(`🔵 1. Fetching user details for email: ${userEmail}`);
        
        // 2. Call your 3040 API to get the companyId
        const userResponse = await axios.get(`https://api.coach.getboarded.com/users?email=${userEmail}`, { headers });
        
        // Safely extract the companyId (handles arrays, paginated objects, or direct objects)
        let companyId = null;
        const resData = userResponse.data;
        
        if (resData && resData.data && resData.data.length > 0) {
            companyId = resData.data[0].companyId; // Paginated response format
        } else if (Array.isArray(resData) && resData.length > 0) {
            companyId = resData[0].companyId; // Direct array format
        } else if (resData && resData.companyId) {
            companyId = resData.companyId; // Direct object format
        }

        if (!companyId) {
          console.error("🔴 Could not find companyId in the users API response.", resData);
          return; // Stop here if no companyId is found
        }

        console.log(`🟢 2. Found companyId: ${companyId}. Now fetching org insights...`);

        // 3. Fetch the organization insights using the retrieved companyId
        const insightsResponse = await axios.get(`https://parser.ai.getboarded.com/api/v2/organization/${companyId}/insights`, { headers });

        console.log("🟢 3. Org Insights Response Success!");

        // Safely assign the documents to the UI
        if (insightsResponse.data && insightsResponse.data.status === 'success') {
          this.orgInsights = insightsResponse.data.data || [];
        } else if (Array.isArray(insightsResponse.data)) {
          this.orgInsights = insightsResponse.data;
        }

      } catch (error) {
        console.error("🔴 API Call Failed:", error);
      }
    },
    openOrgMaterial(url) {
      if(url) {
        if (url.startsWith('/uploads')) {
           window.open(`https://parser.ai.getboarded.com${url}`, '_blank');
        } else {
           window.open(url, '_blank');
        }
      }
    },

    getFileIcon(type) {
      if (type === 'pdf') return 'FileTextIcon';
      if (type === 'word') return 'FileIcon';
      if (type === 'excel') return 'PieChartIcon';
      if (type === 'video') return 'VideoIcon';
      if (type === 'link') return 'LinkIcon';
      return 'FileIcon';
    },
    getFileColor(type) {
      if (type === 'pdf') return 'text-danger';
      if (type === 'word') return 'text-primary';
      if (type === 'excel') return 'text-success';
      if (type === 'video') return 'text-warning';
      if (type === 'link') return 'text-dark';
      return 'text-primary';
    },
    getIconBackground(type) {
      if (type === 'pdf') return 'bg-danger-light';
      if (type === 'word') return 'bg-primary-light';
      if (type === 'excel') return 'bg-success-light';
      if (type === 'video') return 'bg-warning-light';
      if (type === 'link') return 'bg-gray-200';
      return 'bg-primary-light';
    },
    getCategoryClass(category) {
      if (category === 'Video') return 'bg-warning-light text-warning';
      if (category === 'Presentation') return 'bg-success-light text-success';
      if (category === 'Book') return 'bg-primary-light text-primary';
      return 'bg-gray-200 text-gray-700'; // Document
    },
    updatePrimaryColor(color) {
      this.primaryColor = color;
      this.$vs.theme({ primary: color });
    },
    presentationShow(item) {
      this.flag = true;
      this.psrc = item.url;
    },
    videoPlay(item) {
      this.flag1 = true;
      this.vsrc = this.getEmbedUrl(item.url); // Automatically format the URL
    },
    getEmbedUrl(url) {
      if (!url) return '';
      
      // Regular expression to extract the YouTube video ID from various link formats
      // Handles: youtube.com/watch?v=, youtu.be/, youtube.com/embed/
      const ytMatch = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i);
      
      if (ytMatch && ytMatch[1]) {
        // If it's a YouTube link, force it into the embed format
        return `https://www.youtube.com/embed/${ytMatch[1]}`;
      }
      
      // If it's not a YouTube link (e.g., an internal .mp4 upload), just return the original URL
      // Make sure internal paths are properly prefixed if they are relative
      if (url.startsWith('/uploads')) {
         return `https://parser.ai.getboarded.com${url}`;
      }
      
      return url;
    },
  },

  created() {
    if (window.$cookies && window.$cookies.get) {
      this.companyName = window.$cookies.get("companyName");
    }
    // Fire our new, powerful API chain immediately
    this.fetchOrgInsights();
  },  
  components: { VuePerfectScrollbar, VueSlickCarousel, Carousel, Slide }
};
</script>

<style scoped>
.truncate-pm {
  width: 80%;
  white-space: normal !important;
  margin-left: 1.5rem;
}
.items-left {
  text-align: left;
  text-align: -moz-left;
  text-align: -webkit-left;
}
.items-center {
  text-align: center;
  text-align: -moz-center;
  text-align: -webkit-center;
}

.VueCarousel >>> .VueCarousel-navigation-button {

}

.VueCarousel >>> .VueCarousel-navigation-button:hover {
  height: 75%;
  outline: 0;
}

.bg-primary-light { background-color: rgba(var(--vs-primary), 0.12) !important; }
.bg-success-light { background-color: rgba(var(--vs-success), 0.12) !important; }
.bg-warning-light { background-color: rgba(var(--vs-warning), 0.12) !important; }
.bg-danger-light  { background-color: rgba(var(--vs-danger), 0.12) !important; }

.truncate-pm { width: 80%; white-space: normal !important; margin-left: 1.5rem; }
.VueCarousel >>> .VueCarousel-navigation-button:hover { height: 75%; outline: 0; }
</style>
